// assistantThreadBlocks.js — INLINE GENERATIVE BLOCKS in the assistant's thread
// (AI_ASSISTANT_SPEC v1 §3 "inline in the thread (main + sidebar), not only in
// the split pane"; DESIGN-WL-ASSISTANT-MAIN-VIEW-1, lane current-23-assistant).
//
// A block is a card the assistant attaches to its message (ChatCard renders it
// through components/AssistantThreadBlock.js):
//   { type: 'view', route, title }        an answer view (render_answer_ui) —
//                                         the audited DOMQL def the PageSlot
//                                         mounts, drawn INLINE in the thread
//   { type: 'records', block, title? }    a RECORDS BLOCK v1 (functions/
//                                         assistantRecords.js)
//   { type: 'board', boardId, title, summary, count }   a generated board
//   { type: 'widget', widget, params, size?, title? }    an installed app's
//                                         widget with its params (embed_widget,
//                                         functions/_simone/toolEmbed.js) —
//                                         the app's own body mounted inline
// Actions, each only where it means something: Open in split (the view's
// route, the records' app), Save as app (a view → an extension), Save as
// board (a records block → an `ai_boards` record), Refine (the composer asks
// again about this block). Pin to home waits for core's widget recipes to
// accept a records source (widgetDefSchema allows workspaceProject.records /
// people lists only; a pinned block would lose its filters).
//
// Frank note: el.call serializes each registered export on its own (FA204),
// so every export here is self-contained — the few literals are restated.

// The block's title line. A title marked `titleDefault` (the readable default
// functions/_simone/toolEmbed.js gives every embed card, so every host shows
// one) yields to the block's own: a records block's (bundle-keyed) title, an
// installed widget's title; it shows where those are missing.
export const assistantBlockTitle = function assistantBlockTitle(s) {
  const x = s && typeof s === 'object' ? s : {}
  const own = x.title && !x.titleDefault ? String(x.title) : ''
  if (x.type === 'records' && !own) {
    return this.call('recordsBlockTitle', this.call('recordsBlockOf', x))
  }
  if (x.type === 'widget' && !own) {
    const row = this.call('assistantEmbedWidgetRow', x)
    if (row && row.title) return String(this.call('polyglot', row.title))
  }
  if (x.type === 'sources' && !own) return this.call('assistantSourcesTitle', x)
  if (x.type === 'form' && !own) {
    const entry = this.call('assistantFormEntry', x)
    if (entry && entry.form && entry.form.title) {
      return String(this.call('polyglot', entry.form.title))
    }
  }
  return x.title ? String(x.title) : ''
}

// The installed app a records or widget block comes from — its
// `root.workspaceApps` entry, matched by the app's id, its key, or its short
// name ("crm" for system/crm-app); null for a view or a board block, and for
// an app not installed here. The widget's app is its resolved row's module,
// else the prefix of the widget's reference ("crm.crm-pipeline" → crm).
export const assistantBlockApp = function assistantBlockApp(s) {
  const x = s && typeof s === 'object' ? s : {}
  let ref = ''
  if (x.type === 'records') {
    const block = this.call('recordsBlockOf', x)
    ref = block && block.app ? String(block.app) : ''
  } else if (x.type === 'widget') {
    const row = this.call('assistantEmbedWidgetRow', x)
    ref = row && row.moduleId ? String(row.moduleId) : String(x.widget || '').split('.')[0]
  } else if (x.type === 'form') {
    const entry = this.call('assistantFormEntry', x)
    ref = entry && entry.appId ? String(entry.appId) : String(x.form || '').split('.')[0]
  }
  if (!ref) return null
  const root = this.state && this.state.root
  const apps = root && Array.isArray(root.workspaceApps) ? root.workspaceApps : []
  const names = (a) => {
    const id = a && a.id ? String(a.id) : ''
    const key = id.split('/').pop()
    return id === ref || key === ref || key.replace(/-app$/, '') === ref
  }
  const installed = apps.find(names)
  if (installed) return installed
  // A built-in package's view or form (calendar, tickets): its provider's app.
  if (x.type !== 'widget' && x.type !== 'form') return null
  const sources = this.call(
    x.type === 'form' ? 'assistantAppFormSources' : 'assistantAppViewSources',
    root
  )
  const src = (Array.isArray(sources) ? sources : []).find((v) => v && names(v.app))
  return (src && src.app) || null
}

// The block's app glyph for the calm tile in its head (components/AppGlyphTile):
// the app's own launcher icon (its manifest `apps[]` tile), else a navbar
// entry's; '' — no tile — for a view, a board, or an app not installed here.
export const assistantBlockIcon = function assistantBlockIcon(s) {
  // "What can you do here?" (the capabilities catalogue) answers under the
  // glyph of the row that asked it — never a tile with no glyph (the nameless
  // Icon warned on dev 2026-10-04; DESIGN-COMPOSER-FOCUS-POPOVER-CONTEXT-INSPECT-1)
  if (s && s.type === 'capabilities') {
    return this.call('glyphNameFor', { glyph: 'help' }, 'ln') || ''
  }
  const app = this.call('assistantBlockApp', s)
  if (!app) return ''
  const tiles = [
    ...(Array.isArray(app.apps) ? app.apps : []),
    ...(Array.isArray(app.navbar) ? app.navbar : [])
  ]
  const hit = tiles.find((t) => t && typeof t.icon === 'string' && t.icon)
  return hit ? hit.icon : ''
}

// The source line at the foot of a block: where it comes from.
export const assistantBlockSource = function assistantBlockSource(s) {
  const x = s && typeof s === 'object' ? s : {}
  if (x.type === 'view') return String(this.call('polyglot', 'workspace.assistantBlock.sourceView'))
  if (x.type === 'board') {
    return String(this.call('polyglot', 'workspace.assistantBlock.sourceBoard')).replace(
      '{count}',
      String(Number(x.count) || 0)
    )
  }
  if (x.type === 'sources') return this.call('assistantSourcesFoot', x)
  if (x.type !== 'widget' && x.type !== 'records' && x.type !== 'form') return ''
  const app = this.call('assistantBlockApp', x)
  // The app by its App Store name, in the session language (installedAppName
  // — Nika 2026-10-23: "app names = what a person sees in the workspace App
  // Store"): the foot read "From Analytics · widget" for Insights, the
  // manifest's first tab. An app the store ships no name for reads its own
  // first tab through polyglot, as before.
  const nav = app && Array.isArray(app.navbar) ? app.navbar : []
  const tab = nav.find((n) => n && typeof n.label === 'string' && n.label)
  const named =
    (app && String(this.call('installedAppName', app) || '')) ||
    (tab ? String(this.call('polyglot', tab.label)) : '')
  if (x.type === 'widget' || x.type === 'form') {
    const row = x.type === 'widget' ? this.call('assistantEmbedWidgetRow', x) : null
    const ref =
      row && row.moduleId
        ? row.moduleId
        : String((x.type === 'form' ? x.form : x.widget) || '').split('.')[0]
    const appName = named || String(ref).split('/').pop().replace(/-app$/, '')
    // A VIEW is the app's own UI, not a widget: "From Calendar"; a form says so.
    const key =
      x.type === 'form'
        ? 'workspace.assistantBlock.sourceForm'
        : row && row.view
          ? 'workspace.assistantBlock.sourceAppView'
          : 'workspace.assistantBlock.sourceWidget'
    return String(this.call('polyglot', key)).replace('{app}', appName)
  }
  const block = this.call('recordsBlockOf', x)
  if (!block) return ''
  const appName = named || block.app
  return String(this.call('polyglot', 'workspace.assistantBlock.sourceRecords'))
    .replace('{app}', appName)
    .replace(
      '{collection}',
      this.call('recordsBlockTitle', { app: block.app, collection: block.collection })
    )
}

// Which actions a block offers: open | saveApp | saveBoard | refine.
export const assistantBlockCan = function assistantBlockCan(s, action) {
  const x = s && typeof s === 'object' ? s : {}
  const root = this.state && this.state.root
  if (action === 'refine') {
    return (
      x.type === 'view' ||
      x.type === 'records' ||
      x.type === 'board' ||
      x.type === 'widget' ||
      x.type === 'form' ||
      x.type === 'sources'
    )
  }
  if (x.type === 'widget') {
    if (action !== 'open') return false
    const row = this.call('assistantEmbedWidgetRow', x)
    return !!(row && row.openRoute)
  }
  if (x.type === 'view') {
    const def = root && root._aiAnswerPages ? root._aiAnswerPages[x.route] : null
    if (action === 'open') return !!def
    // Save as app: an answer view becomes an extension (its source), and a
    // BUILT app (Apps mode, create_extension / create_app — the preview's
    // `__extensionApp`) is installed into the workspace.
    const meta = def && def.metadata
    const built = meta && meta.__extensionApp && meta.__extensionApp.projectId
    if (action === 'saveApp') return !!(meta && (meta.__source || built))
    return false
  }
  if (x.type === 'records') {
    const block = this.call('recordsBlockOf', x)
    if (!block) return false
    if (action === 'open') return !!this.call('assistantRecordsHref', block, null)
    if (action === 'saveBoard') return true
    return false
  }
  if (x.type === 'board') {
    if (action !== 'open' || !x.boardId) return false
    // Only a board this session still holds can be shown again (the thread
    // chips' history, workspace-scoped and in memory).
    const list = root && Array.isArray(root._aiBoardHistory) ? root._aiBoardHistory : []
    return list.some((h) => h && h.id === x.boardId && h.raw)
  }
  return false
}

// Open in split: a view's own route, the records' app, a board over the home.
export const assistantBlockOpen = function assistantBlockOpen(s) {
  const x = s && typeof s === 'object' ? s : {}
  if (x.type === 'view' && x.route) {
    this.call('openInSlot', x.route, this)
    return true
  }
  if (x.type === 'records')
    return this.call('assistantRecordsOpen', this.call('recordsBlockOf', x), null)
  if (x.type === 'widget') {
    const row = this.call('assistantEmbedWidgetRow', x)
    if (!row || !row.openRoute) return false
    // A view's own page with the filters the view resolved, when its mounted
    // body declares it (functions/assistantAppViews.js `openRoute`).
    const host = typeof this.lookup === 'function' ? this.lookup('BlockWidget') : null
    const own = this.call('assistantAppViewOwnRoute', host)
    this.call('openInSlot', own || row.openRoute, this)
    return true
  }
  if (x.type === 'board' && x.boardId) {
    // The board shows OVER WHATEVER PAGE the person is on (AI_ASSISTANT_SPEC
    // §12.2, "Everywhere"; DESIGN-WL-ASSISTANT-BOARD-OFF-HOME-PAGESLOT-1): the
    // drawer closes (a drawer promoted out of the main placement returns to
    // it, the shell's close edge); a sidebar the person CHOSE keeps its
    // placement and the board shows through the board view's own preview
    // mode on the home — the one route its preview can show on, so that case
    // alone still goes home (the router, the way the header's Home button
    // navigates).
    const root = this.state && this.state.root
    if (!root || typeof root.update !== 'function') return false
    const shown = this.call('assistantBoardShow', x.boardId)
    if (!shown) return false
    const onMain = root.assistantDock === 'main' || root._assistantDockPromotedFrom === 'main'
    root.update(
      {
        assistantOpen: false,
        _homeThreadOpen: false,
        ...(onMain ? {} : { _aiBoardPreview: true, _aiBoard: { ...root._aiBoard, path: '/' } })
      },
      { preventFetch: true }
    )
    if (onMain) return true
    const raw = typeof root.currentPath === 'string' && root.currentPath ? root.currentPath : '/'
    const stripped = this.call('stripWorkspacePrefix', raw)
    const path = typeof stripped === 'string' ? stripped : raw
    if (path !== '/' && path !== '' && typeof this.router === 'function') {
      try {
        this.router('/', typeof this.getRoot === 'function' ? this.getRoot() : undefined)
      } catch (_) {
        /* the router is not reachable here — the board waits on the home */
      }
    }
    return true
  }
  return false
}

// Save as app: the answer view becomes an extension (the PageSlot's own
// "Save as app" bridge, functions/extensionSaveShare.js). A BUILT app — Apps
// mode's create_extension / create_app preview (AI_ASSISTANT_SPEC §12.1: "then
// 'Save as app' installs it") — is already a saved draft: it is INSTALLED
// through the one existing path, the confirmed Add to Workspace
// (functions/workspaceApps.js addExtensionToWorkspace: the confirm dialog, the
// share, the CAS-guarded apps write, the hot activation).
export const assistantBlockSaveApp = function assistantBlockSaveApp(s) {
  const x = s && typeof s === 'object' ? s : {}
  if (x.type !== 'view' || !x.route) return null
  const root = this.state && this.state.root
  const def = root && root._aiAnswerPages ? root._aiAnswerPages[x.route] : null
  const app = def && def.metadata && def.metadata.__extensionApp
  if (app && app.projectId) {
    return this.call('addExtensionToWorkspace', { projectId: app.projectId, name: app.name })
  }
  return this.call('saveAnswerViewAsApp', x.route)
}

// The Save-as-app button's word and state for a block: a built app already
// installed reads "Added to workspace" (and rests), a record still in flight
// rests too (never a second install over an unread list).
export const assistantBlockSaveAppState = function assistantBlockSaveAppState(s) {
  const x = s && typeof s === 'object' ? s : {}
  const root = this.state && this.state.root
  const def = x.type === 'view' && root && root._aiAnswerPages ? root._aiAnswerPages[x.route] : null
  const app = def && def.metadata && def.metadata.__extensionApp
  if (!app || !app.projectId) return 'save'
  const fns = this.context && this.context.functions
  if (
    fns &&
    typeof fns.isExtensionAddedPending === 'function' &&
    this.call('isExtensionAddedPending', root)
  ) {
    return 'checking'
  }
  if (fns && typeof fns.isExtensionAddedToWorkspace === 'function') {
    if (this.call('isExtensionAddedToWorkspace', root, app.projectId)) return 'added'
  }
  return 'save'
}

// The save state of one block — 'saving' | 'saved' | 'failed' | null.
export const assistantBlockSaveState = function assistantBlockSaveState(root, s) {
  const r = root && typeof root === 'object' ? root : {}
  const bag = r._aiBlockSaves && typeof r._aiBlockSaves === 'object' ? r._aiBlockSaves : {}
  const block = this.call('recordsBlockOf', s)
  if (!block) return null
  return bag[this.call('boardSpecHash', block)] || null
}

// Save as board: ONE `ai_boards` record whose one item is this records block
// (the server validates it against the collection's fields).
export const assistantBlockSaveBoard = async function assistantBlockSaveBoard(el, s) {
  const host = el || this
  const root = (s && s.root) || (host && host.state && host.state.root)
  const block = host.call('recordsBlockOf', s)
  if (!root || typeof root.update !== 'function' || !block)
    return { ok: false, reason: 'unavailable' }
  const key = host.call('boardSpecHash', block)
  const mark = (state) => {
    const bag =
      root._aiBlockSaves && typeof root._aiBlockSaves === 'object' ? root._aiBlockSaves : {}
    root.update({ _aiBlockSaves: { ...bag, [key]: state } }, { preventFetch: true })
  }
  const now = (root._aiBlockSaves || {})[key]
  if (now === 'saving' || now === 'saved') return { ok: now === 'saved', already: true }
  const title = host.call('assistantBlockTitle', s) || host.call('recordsBlockTitle', block)
  const ws = root.workspace
  const workspaceId = (ws && (ws.id || ws._id)) || null
  mark('saving')
  try {
    const sdk = host.sdk || host.call('getSDK')
    if (!sdk || typeof sdk.execute !== 'function') throw new Error('unknown method: ai.boards')
    const saved = await sdk.execute('ai.boards', 'save', {
      spec: { v: 1, prompt: title, title, summary: '', items: [{ ...block }] },
      title,
      ...(workspaceId ? { workspaceId } : {})
    })
    const row = saved && typeof saved === 'object' ? saved.data || saved : null
    if (row && row.id) {
      const list = Array.isArray(root._aiBoards) ? root._aiBoards : []
      root.update(
        { _aiBoards: [row, ...list.filter((b) => b && b.id !== row.id)] },
        { preventFetch: true }
      )
    }
    mark('saved')
    return { ok: true, board: row }
  } catch (err) {
    mark('failed')
    return { ok: false, reason: 'failed', error: (err && err.message) || String(err) }
  }
}

// The Save-as-board button's label index (AppLabelStack: save · saving · saved).
export const assistantBlockSaveLabel = function assistantBlockSaveLabel(root, s) {
  const state = this.call('assistantBlockSaveState', root, s)
  return state === 'saving' ? 1 : state === 'saved' ? 2 : 0
}

// Refine: the composer asks again about THIS block — its words prefilled
// ("Refine “Q3 viewings”: "), the cursor at the end, the send stays the
// person's. The composer is found through the DOMQL tree, never a selector.
export const assistantBlockRefine = function assistantBlockRefine(s) {
  const title = this.call('assistantBlockTitle', s)
  const text = String(this.call('polyglot', 'workspace.assistantBlock.refinePrefix')).replace(
    '{title}',
    title
  )
  try {
    const root = typeof this.getRoot === 'function' ? this.getRoot() : null
    const composer = root && root.lookdown ? root.lookdown('CanvasPromptTextarea') : null
    if (composer && composer.state && typeof composer.state.update === 'function') {
      composer.state.update({ draft: text })
    }
    const field = composer && composer.lookdown ? composer.lookdown('Textarea') : null
    const node = field && field.node
    if (node && typeof node.focus === 'function') {
      node.focus()
      if (typeof node.setSelectionRange === 'function')
        node.setSelectionRange(text.length, text.length)
    }
    return !!composer
  } catch (_) {
    return false
  }
}

// An answer view INLINE: the audited def the PageSlot mounts (renderAnswerUi →
// `root._aiAnswerPages[route]`), mounted into this host — without its helmet
// `metadata` (an inline view never takes the tab title) and without page-grid
// placement. The def's context rides explicitly (non-enumerable on module
// defs, the PageSlot's note). Once per route; a route that is gone (the map
// is session-transient and pruned) leaves the host empty and the block says so.
export const assistantBlockMountView = function assistantBlockMountView(el, s) {
  const host = el || this
  if (!host || typeof host.set !== 'function' || !s || s.type !== 'view') return false
  const root = s.root
  // A view card that carries its SOURCE and no route — a Fly page's UI
  // section on a reloaded thread (functions/_simone/toolEmbed.js
  // flyPageEmbeds): the answer view is registered again through the same
  // audit and gate (renderAnswerUi, not opened), once per host, and the card
  // learns its route (Open in split and Save as app read it). A source the
  // gate refuses says one quiet line.
  let route = typeof s.route === 'string' ? s.route : ''
  if (!route && typeof s.source === 'string' && s.source.trim()) {
    const want = 'source/' + this.call('boardSpecHash', s.source)
    if (host.__blockViewSource !== want) {
      host.__blockViewSource = want
      let res = null
      try {
        res = this.call('renderAnswerUi', {
          title: typeof s.title === 'string' ? s.title : '',
          source: s.source,
          placement: s.placement === 'split' ? 'split' : 'inline',
          open: false
        })
      } catch (_) {
        res = null
      }
      host.__blockViewRoute =
        res && res.ok === true && typeof res.opened === 'string' ? res.opened : ''
      if (!host.__blockViewRoute) {
        host.__blockViewSig = 'refused'
        try {
          host.set({
            tag: 'p',
            margin: '0',
            fontSize: 'Z',
            color: 'caption',
            text: String(this.call('polyglot', 'workspace.assistantBlock.viewFailed'))
          })
        } catch (_) {
          /* the host is gone */
        }
        return false
      }
      if (typeof s.update === 'function') {
        try {
          s.update({ route: host.__blockViewRoute }, { preventFetch: true })
        } catch (_) {
          /* the card's state is gone — the host keeps its route */
        }
      }
    }
    route = host.__blockViewRoute || ''
    if (!route) return false
  }
  const def = route && root && root._aiAnswerPages ? root._aiAnswerPages[route] : null
  const sig = def ? 'view/' + route : 'none'
  if (host.__blockViewSig === sig) return !!def
  host.__blockViewSig = sig
  if (!def) return false
  const rest = { ...def }
  delete rest.metadata
  host.set({
    ...rest,
    context: def.context,
    tag: 'div',
    gridRow: null,
    gridColumn: null,
    height: 'auto',
    minHeight: '0'
  })
  return true
}

// Is the view's def still on this session (the block shows it, else a note)?
export const assistantBlockViewGone = function assistantBlockViewGone(s) {
  if (!s || s.type !== 'view') return false
  // A card that carries its source is drawn from it (assistantBlockMountView
  // above registers it again, or says the gate refused it).
  if (typeof s.source === 'string' && s.source.trim()) return false
  const root = s.root
  return !(root && root._aiAnswerPages && root._aiAnswerPages[s.route])
}

// ── A WIDGET embed (embed_widget → { type: 'widget', widget, params, size?,
// title? }, functions/_simone/toolEmbed.js) ───────────────────────────────────
// Resolved against THIS workspace's installed widgets through the board's own
// derivation (boardItemsFor: the widget validated, its params checked against
// the widget's schema, the body and the app it mounts with) — so the thread
// mounts exactly what a generated board would. null while the apps have not
// published (`workspaceApps` null), or when the widget or its params are not
// installed here.
export const assistantEmbedWidgetRow = function assistantEmbedWidgetRow(s) {
  const x = s && typeof s === 'object' ? s : {}
  if (x.type !== 'widget' || typeof x.widget !== 'string' || !x.widget) return null
  const root = this.state && this.state.root
  if (!root) return null
  const params =
    x.params && typeof x.params === 'object' && !Array.isArray(x.params) ? x.params : {}
  const spec = {
    v: 1,
    items: [{ kind: 'widget', widget: x.widget, params, ...(x.size ? { size: x.size } : {}) }]
  }
  const derived = this.call('boardItemsFor', spec, root)
  const row = derived && Array.isArray(derived.items) ? derived.items[0] : null
  return row && row.kind === 'widget' ? row : null
}

// A widget card's state: 'loading' while an INSTALLED app has not published
// (or is still converging), 'ready', 'failed' (its installed app did not load),
// 'denied' (a view this member may not see — its `access`), or 'refused' (not
// installed here, or params the widget does not take); null
// for any other card. "Installed" is the ONE shared reader of the workspace's
// declared app entries (workspaceConfigs.js installedAppRefs — never the
// server-managed records-grant row), with the homeSignals.js rule: the apps
// are known when nothing is declared or the loaded list has published. The
// loader publishes only the apps that LOADED (functions/workspaceProjects.js);
// its record of this workspace's apps phase names the installed ones it
// refused (`_workspaceActivation.apps.blocked`, `owner/key` ids).
export const assistantEmbedWidgetStatus = function assistantEmbedWidgetStatus(s) {
  const x = s && typeof s === 'object' ? s : {}
  if (x.type !== 'widget') return null
  const root = this.state && this.state.root
  if (!root) return 'loading'
  const settings = (root.workspace && root.workspace.settings) || {}
  const refs = this.call('installedAppRefs', settings)
  const declared = Array.isArray(refs) ? refs : []
  if (declared.length && !Array.isArray(root.workspaceApps)) return 'loading'
  if (this.call('assistantEmbedWidgetRow', x)) return 'ready'
  const ref = String(x.widget || '').split('.')[0]
  const names = (id) => {
    const full = String(id || '')
    const key = full.split('/').pop()
    return full === ref || key === ref || key.replace(/-app$/, '') === ref
  }
  const ws = root.workspace
  const live = ws && (ws.id || ws._id) ? String(ws.id || ws._id) : ''
  const act = root._workspaceActivation
  const blocked =
    act &&
    live &&
    String(act.workspaceId || '') === live &&
    act.apps &&
    Array.isArray(act.apps.blocked)
      ? act.apps.blocked
      : []
  if (blocked.some(names)) return 'failed'
  // A view this member may not see (its `access`, functions/assistantAppViews.js).
  const vocab = this.call('boardVocabularyFor', root)
  const id = String(x.widget || '')
    .split('.')
    .pop()
  if (vocab && vocab.denied && Object.prototype.hasOwnProperty.call(vocab.denied, id)) {
    return 'denied'
  }
  // installed here, not published, not refused: the loader is still converging
  if (declared.some((c) => c && names(String(c.owner || '') + '/' + String(c.key || '')))) {
    return 'loading'
  }
  return 'refused'
}

// The one sentence a widget card says instead of its body: its app did not
// load here, or it is not installed here; '' while loading or ready.
export const assistantEmbedWidgetNote = function assistantEmbedWidgetNote(s) {
  const status = this.call('assistantEmbedWidgetStatus', s)
  if (status === 'failed') {
    return String(this.call('polyglot', 'workspace.assistantBlock.widgetFailed'))
  }
  if (status === 'refused') {
    return String(this.call('polyglot', 'workspace.assistantBlock.widgetGone'))
  }
  if (status === 'denied') {
    return String(this.call('polyglot', 'workspace.assistantBlock.viewDenied'))
  }
  return ''
}

// Mount the widget's OWN body into the card — the home's and the board's
// mount (assistantBoardMountBody: the app's context, `scope.params` = the
// schema defaults + the card's params). Signature-guarded there, so calling it
// on every update mounts once per widget + params.
export const assistantEmbedWidgetMount = function assistantEmbedWidgetMount(el, s) {
  const row = this.call('assistantEmbedWidgetRow', s)
  if (!row) return false
  const root = this.state && this.state.root
  this.call('assistantBoardMountBody', el, { ...row, root })
  return true
}
